/**
 * The rest of the strike group.
 *
 * A carrier does not steam alone, and the reason to model that here is not
 * decoration. An empty ocean gives you nothing to judge distance or motion
 * against — the sea has no scale, the horizon has no features, and a ship five
 * miles away looks exactly like a ship one mile away. Put two escorts on the
 * water and the whole picture acquires depth.
 *
 * Positions are in the SHIP frame, feet: origin midships on the centreline,
 * +X forward, +Y starboard. They stay fixed relative to the carrier because a
 * screen keeps station — that is the entire job.
 *
 * The one that matters operationally is the PLANE GUARD. During flight
 * operations a destroyer sits astern and to port of the carrier, far enough
 * back to be clear of the pattern and close enough to pull a crew out of the
 * water. Its position is not arbitrary: it must be clear of the extended
 * angled-deck centreline, because that is where aeroplanes are, and clear of
 * the wake, because that is where the carrier is going to be. `npm test`
 * checks both.
 */

/** @typedef {{name: string, x: number, y: number, lengthFt: number, beamFt: number, kind: string}} Escort */

/** @type {Escort[]} */
export const ESCORTS = [
  {
    name: 'PLANE GUARD',
    kind: 'destroyer',
    // A mile and a quarter astern, well out to port. Astern so it is behind
    // the ship it is guarding; to port because the groove and the landing area
    // are to port, and the one place a rescue ship must never be is under the
    // aeroplane it might have to rescue.
    x: -7600,
    y: -3400,
    lengthFt: 505,
    beamFt: 66,
  },
  {
    name: 'ESCORT',
    kind: 'cruiser',
    // Ahead and to starboard, screening the direction the group is going.
    x: 11800,
    y: 5200,
    lengthFt: 567,
    beamFt: 55,
  },
]

/**
 * How far is this escort from the extended angled-deck centreline?
 *
 * The groove runs aft from the ramp along a line 9 degrees to port of the
 * hull. Anything sitting on it is under the approach.
 */
export function distanceFromGrooveFt(escort, angleDeg = 9, rampX = -545, rampY = 42) {
  const th = (-angleDeg * Math.PI) / 180
  const dx = escort.x - rampX
  const dy = escort.y - rampY
  // Perpendicular distance from the deck centreline, and how far astern.
  const alongDeck = dx * Math.cos(th) + dy * Math.sin(th)
  const across = -dx * Math.sin(th) + dy * Math.cos(th)
  return { alongDeck, across: Math.abs(across) }
}
